import React, { useState, useEffect } from 'react';
import api from '../api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Eye, Image } from '@phosphor-icons/react';
import SolicitarAtendimento from './SolicitarAtendimento';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';

const MinhasSolicitacoes = ({ clientId }) => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [isAprovarOpen, setIsAprovarOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [relatorio, setRelatorio] = useState(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [showRelatorio, setShowRelatorio] = useState(false);
  const [fotoExpandida, setFotoExpandida] = useState(null);

  useEffect(() => {
    fetchSolicitacoes();
  }, [clientId]);

  const fetchSolicitacoes = async () => {
    try {
      const response = await api.get(`/solicitacoes?client_id=${clientId}`);
      setSolicitacoes(response.data);
    } catch (error) {
      console.error('Error fetching solicitacoes:', error);
    }
  };

  const fetchRelatorio = async (solicitacaoId) => {
    setLoadingRelatorio(true);
    try {
      const response = await api.get(`/solicitacoes/${solicitacaoId}/relatorio`);
      setRelatorio(response.data);
    } catch (error) {
      console.error('Error fetching relatorio:', error);
      setRelatorio(null);
    } finally {
      setLoadingRelatorio(false);
    }
  };

  const handleOpenAprovar = async (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setIsAprovarOpen(true);
    setShowRelatorio(false);
    setRelatorio(null);
    await fetchRelatorio(solicitacao.id);
  };

  const handleAprovar = async (aprovado) => {
    try {
      await api.post(`/solicitacoes/${selectedSolicitacao.id}/aprovar`, {
        atendimento_id: '',
        solicitacao_id: selectedSolicitacao.id,
        aprovado: aprovado,
        feedback: feedback || null
      });

      setIsAprovarOpen(false);
      setSelectedSolicitacao(null);
      setFeedback('');
      setRelatorio(null);
      fetchSolicitacoes();
      
      if (aprovado) {
        alert('Serviço aprovado! As horas foram descontadas do seu plano.');
      } else {
        alert('Serviço rejeitado. A solicitação foi cancelada.');
      }
    } catch (error) {
      console.error('Error aprovando serviço:', error);
      alert('Erro ao processar aprovação: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'pendente': 'bg-yellow-500 text-black',
      'atribuida': 'bg-blue-500',
      'em_andamento': 'bg-purple-500',
      'aguardando_aprovacao': 'bg-orange-500',
      'aprovada': 'bg-green-500',
      'concluida': 'bg-emerald-600',
      'cancelada': 'bg-red-500'
    };
    const labels = {
      'pendente': 'Aguardando Atribuição',
      'atribuida': 'Técnico Atribuído',
      'em_andamento': 'Em Andamento',
      'aguardando_aprovacao': 'Aguardando Sua Aprovação',
      'aprovada': 'Aprovada',
      'concluida': 'Concluída',
      'cancelada': 'Cancelada'
    };
    return <Badge className={colors[status]}>{labels[status]}</Badge>;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Minhas Solicitações
        </h3>
        <SolicitarAtendimento clientId={clientId} onSuccess={fetchSolicitacoes} />
      </div>

      {solicitacoes.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8 text-center">
          <div className="text-zinc-400 mb-4">Você ainda não tem solicitações</div>
          <SolicitarAtendimento clientId={clientId} onSuccess={fetchSolicitacoes} />
        </div>
      ) : (
        <div className="space-y-4">
          {solicitacoes.map(solicitacao => (
            <div key={solicitacao.id} className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid={`solicitacao-${solicitacao.numero_solicitacao}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-2xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {solicitacao.numero_solicitacao}
                  </div>
                  <div className="text-zinc-400 text-sm mt-1">
                    {new Date(solicitacao.created_at).toLocaleString('pt-BR')}
                  </div>
                </div>
                {getStatusBadge(solicitacao.status)}
              </div>

              {solicitacao.prestador_nome && (
                <div className="mb-4">
                  <div className="text-zinc-400 text-xs mb-1">Técnico Responsável</div>
                  <div className="text-white font-medium">{solicitacao.prestador_nome}</div>
                </div>
              )}

              <div className="mb-4">
                <div className="text-zinc-400 text-xs mb-2">Serviços Solicitados</div>
                <div className="flex flex-wrap gap-2">
                  {solicitacao.servicos_solicitados.map((servico, idx) => (
                    <span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded text-zinc-400">
                      {servico}
                    </span>
                  ))}
                </div>
              </div>

              {solicitacao.urgencia && (
                <div className="mb-4">
                  <span className={`text-xs px-2 py-1 rounded ${
                    solicitacao.urgencia === 'urgente' ? 'bg-red-500' :
                    solicitacao.urgencia === 'rapido' ? 'bg-orange-500' :
                    solicitacao.urgencia === 'programado' ? 'bg-blue-500' :
                    'bg-zinc-500'
                  }`}>
                    {solicitacao.urgencia.toUpperCase()}
                  </span>
                </div>
              )}

              {solicitacao.horas_executadas > 0 && (
                <div className="mb-4 p-3 bg-[#050505] rounded-sm">
                  <div className="text-zinc-400 text-xs mb-1">Horas Executadas</div>
                  <div className="text-[#FFB800] text-2xl font-bold">{solicitacao.horas_executadas.toFixed(1)}h</div>
                </div>
              )}

              {/* Botão Aprovar */}
              {solicitacao.status === 'aguardando_aprovacao' && (
                <Button
                  onClick={() => handleOpenAprovar(solicitacao)}
                  className="bg-[#22C55E] text-white hover:bg-[#16A34A]"
                  data-testid="aprovar-servico-button"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Aprovar Serviço
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal Aprovar/Rejeitar */}
      <Dialog open={isAprovarOpen} onOpenChange={setIsAprovarOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Aprovar Serviço</DialogTitle>
          </DialogHeader>
          {selectedSolicitacao && (
            <div className="space-y-4">
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-sm text-zinc-400 mb-1">Solicitação</div>
                <div className="text-lg font-bold text-[#FFB800]">{selectedSolicitacao.numero_solicitacao}</div>
                <div className="text-sm text-white mt-2">
                  Técnico: {selectedSolicitacao.prestador_nome}
                </div>
                <div className="text-2xl font-bold text-[#22C55E] mt-2">
                  {selectedSolicitacao.horas_executadas.toFixed(1)} horas
                </div>
              </div>

              {/* Ver Relatório Toggle */}
              <Button
                type="button"
                onClick={() => setShowRelatorio(!showRelatorio)}
                className="w-full bg-[#1E1E1E] hover:bg-[#27272A] text-white border border-[#27272A]"
                data-testid="ver-relatorio-button"
              >
                <Eye size={16} className="mr-2" />
                {showRelatorio ? 'Ocultar Relatório' : 'Ver Relatório do Técnico'}
              </Button>

              {/* Relatório Details */}
              {showRelatorio && (
                <div className="border border-[#27272A] rounded-sm overflow-hidden" data-testid="relatorio-detalhes">
                  {loadingRelatorio ? (
                    <div className="p-4 text-center text-zinc-400">Carregando relatório...</div>
                  ) : relatorio ? (
                    <div className="p-4 space-y-4 bg-[#0A0A0A]">
                      {/* Data do Atendimento */}
                      {relatorio.data_atendimento && (
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Data do Atendimento</div>
                          <div className="text-white text-sm">
                            {new Date(relatorio.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      )}

                      {/* Equipe */}
                      <div className="grid grid-cols-2 gap-4">
                        {relatorio.eletricista_nome && (
                          <div>
                            <div className="text-zinc-500 text-xs mb-1">Eletricista</div>
                            <div className="text-white text-sm font-medium">{relatorio.eletricista_nome}</div>
                          </div>
                        )}
                        {relatorio.auxiliar_nome && (
                          <div>
                            <div className="text-zinc-500 text-xs mb-1">Auxiliar</div>
                            <div className="text-white text-sm font-medium">{relatorio.auxiliar_nome}</div>
                          </div>
                        )}
                      </div>

                      {/* Horários */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Chegada</div>
                          <div className="text-white text-sm">
                            {relatorio.horario_chegada ? new Date(relatorio.horario_chegada).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Saída</div>
                          <div className="text-white text-sm">
                            {relatorio.horario_saida ? new Date(relatorio.horario_saida).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </div>
                        </div>
                      </div>

                      {/* Horas Trabalhadas */}
                      <div>
                        <div className="text-zinc-500 text-xs mb-1">Horas Trabalhadas</div>
                        <div className="text-[#FFB800] font-bold">{relatorio.horas_trabalhadas?.toFixed(1)}h</div>
                        {(relatorio.horas_eletricista > 0 || relatorio.horas_auxiliar > 0) && (
                          <div className="text-xs text-zinc-500 mt-1">
                            {relatorio.horas_eletricista > 0 && <span>Eletricista: {relatorio.horas_eletricista?.toFixed(1)}h</span>}
                            {relatorio.horas_auxiliar > 0 && <span className="ml-3">Auxiliar: {relatorio.horas_auxiliar?.toFixed(1)}h</span>}
                          </div>
                        )}
                      </div>

                      {/* Serviços Executados */}
                      <div>
                        <div className="text-zinc-500 text-xs mb-2">Serviços Executados</div>
                        <div className="flex flex-wrap gap-2">
                          {relatorio.servicos_executados?.map((s, idx) => (
                            <span key={idx} className="text-xs bg-[#1E1E1E] border border-[#27272A] px-2 py-1 rounded text-zinc-300">
                              {s.servico || s}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Observações */}
                      {relatorio.observacoes && (
                        <div>
                          <div className="text-zinc-500 text-xs mb-1">Observações do Técnico</div>
                          <div className="text-white text-sm bg-[#121212] p-3 rounded-sm border border-[#27272A]">
                            {relatorio.observacoes}
                          </div>
                        </div>
                      )}

                      {/* Fotos */}
                      {relatorio.fotos_depois && relatorio.fotos_depois.length > 0 && (
                        <div>
                          <div className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                            <Image size={14} />
                            Fotos do Serviço ({relatorio.fotos_depois.length})
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {relatorio.fotos_depois.map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`Foto ${idx + 1}`}
                                className="w-full h-24 object-cover rounded-sm border border-[#27272A] cursor-pointer hover:border-[#FFB800] transition-colors"
                                onClick={() => setFotoExpandida(url)}
                                data-testid={`foto-relatorio-${idx}`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-zinc-500">Relatório não encontrado</div>
                  )}
                </div>
              )}

              <div>
                <Label>Feedback (opcional)</Label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="w-full px-4 py-2 bg-[#050505] border border-[#27272A] rounded-sm text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] outline-none min-h-24"
                  placeholder="Como foi o atendimento? Comentários sobre o serviço..."
                  data-testid="feedback-textarea"
                />
              </div>

              <div className="p-4 bg-yellow-900/20 border border-yellow-500 rounded-sm text-yellow-500 text-sm">
                Ao aprovar, as {selectedSolicitacao.horas_executadas.toFixed(1)} horas serão descontadas do seu plano.
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleAprovar(false)}
                  className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]"
                  data-testid="rejeitar-button"
                >
                  <XCircle size={16} className="mr-2" />
                  Rejeitar
                </Button>
                <Button
                  onClick={() => handleAprovar(true)}
                  className="flex-1 bg-[#22C55E] hover:bg-[#16A34A]"
                  data-testid="aprovar-button"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Aprovar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Foto Expandida */}
      <Dialog open={!!fotoExpandida} onOpenChange={() => setFotoExpandida(null)}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-3xl p-2">
          {fotoExpandida && (
            <img
              src={fotoExpandida}
              alt="Foto expandida"
              className="w-full max-h-[80vh] object-contain rounded-sm"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MinhasSolicitacoes;
